import React, { useState, useRef, useEffect } from 'react';
import { apiPost, apiGet } from '../../utils/api';
import { API } from '../../config';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import {
  Upload, FileText, Mic, MicOff, Type, Loader2, ArrowRight,
  CheckCircle2, AlertCircle, Brain, TrendingUp, X,
} from 'lucide-react';

function ConfidenceGauge({ score, prevScore }) {
  const color = score >= 75 ? 'text-emerald-500' : score >= 50 ? 'text-amber-500' : 'text-red-500';
  const bgColor = score >= 75 ? 'from-emerald-500/10 to-emerald-500/5' : score >= 50 ? 'from-amber-500/10 to-amber-500/5' : 'from-red-500/10 to-red-500/5';
  const borderColor = score >= 75 ? 'border-emerald-500/30' : score >= 50 ? 'border-amber-500/30' : 'border-red-500/30';

  return (
    <div className={`inline-flex flex-col items-center gap-2 px-8 py-5 rounded-2xl border bg-gradient-to-b ${bgColor} ${borderColor}`} data-testid="confidence-gauge">
      <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Company Intelligence Confidence</span>
      <span className={`text-5xl font-bold ${color}`}>{score}%</span>
      {prevScore != null && prevScore !== score && (
        <span className="text-xs text-emerald-500 flex items-center gap-1">
          <TrendingUp className="w-3 h-3" /> +{score - prevScore}% improvement
        </span>
      )}
    </div>
  );
}

function UploadPanel({ onComplete }) {
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState([]);
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    for (const file of files) {
      setUploading(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        const token = localStorage.getItem('odapto_session_token') || sessionStorage.getItem('odapto_session_token');
        const res = await fetch(`${API}/company/documents/upload?doc_type=general`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData,
        });
        if (res.ok) {
          const data = await res.json();
          setUploaded(prev => [...prev, { name: file.name, summary: data.summary || '' }]);
          toast.success(`${file.name} processed`);
        } else {
          const err = await res.json().catch(() => ({}));
          toast.error(err.detail || `Failed to upload ${file.name}`);
        }
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="upload-panel">
      <div
        onClick={() => fileRef.current?.click()}
        className="border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:border-odapto-orange/50 transition-colors"
      >
        <input ref={fileRef} type="file" multiple accept=".pdf,.docx,.doc,.txt,.md,.csv" onChange={handleUpload} className="hidden" />
        <Upload className="w-8 h-8 mx-auto mb-3 text-muted-foreground" />
        <p className="font-medium text-sm mb-1">Drop files here or click to upload</p>
        <p className="text-xs text-muted-foreground">PDF, DOCX, TXT, CSV — up to 10MB each</p>
        <p className="text-[10px] text-muted-foreground mt-2">
          Company Profile, Pitch Deck, Business Plan, Marketing Strategy, Product Catalogue, Revenue Summary...
        </p>
      </div>
      {uploading && (
        <div className="flex items-center gap-2 text-sm text-odapto-orange">
          <Loader2 className="w-4 h-4 animate-spin" /> Processing document...
        </div>
      )}
      {uploaded.length > 0 && (
        <div className="space-y-2">
          {uploaded.map((f, i) => (
            <div key={i} className="flex items-center gap-2 text-sm p-2 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
              <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
              <span className="font-medium">{f.name}</span>
            </div>
          ))}
          <Button onClick={onComplete} className="w-full bg-odapto-orange hover:bg-odapto-orange-hover text-white" data-testid="upload-done-btn">
            Continue <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      )}
    </div>
  );
}

function DescribePanel({ onComplete }) {
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (text.trim().length < 20) { toast.error('Please write at least a few sentences'); return; }
    setSubmitting(true);
    try {
      const res = await apiPost('/company/describe', { text: text.trim() });
      if (res.ok) {
        const data = await res.json();
        toast.success('Business description processed!');
        onComplete(data);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to process description');
      }
    } catch {
      toast.error('Connection error');
    }
    setSubmitting(false);
  };

  return (
    <div className="space-y-4" data-testid="describe-panel">
      <div>
        <p className="text-sm text-muted-foreground mb-3">
          Tell us about your business as if you were explaining it to a new employee.
        </p>
        <div className="text-xs text-muted-foreground mb-3 space-y-1 bg-muted/50 rounded-lg p-3">
          <p className="font-medium text-foreground mb-1">Consider mentioning:</p>
          <p>• What do you do?</p>
          <p>• Who are your customers?</p>
          <p>• What makes you different?</p>
          <p>• What products or services do you offer?</p>
          <p>• Where do you operate?</p>
          <p>• What are your biggest strengths?</p>
          <p>• What challenges are you currently facing?</p>
        </div>
      </div>
      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder="We are a software company based in London that specializes in..."
        rows={8}
        className="w-full rounded-xl border border-border bg-background px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-odapto-orange/30"
        dir="ltr"
        data-testid="describe-textarea"
      />
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{text.length} characters</span>
        <Button onClick={handleSubmit} disabled={submitting || text.trim().length < 20}
          className="bg-odapto-orange hover:bg-odapto-orange-hover text-white" data-testid="describe-submit-btn">
          {submitting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Analysing...</> : <>Analyse Description <ArrowRight className="w-4 h-4 ml-2" /></>}
        </Button>
      </div>
    </div>
  );
}

function VoicePanel({ onComplete }) {
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [duration, setDuration] = useState(0);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.start(1000);
      mediaRef.current = mr;
      setRecording(true);
      setDuration(0);
      timerRef.current = setInterval(() => setDuration(d => d + 1), 1000);
    } catch (err) {
      toast.error('Microphone access denied. Please allow microphone permissions.');
    }
  };

  const stopRecording = async () => {
    if (!mediaRef.current) return;
    setRecording(false);
    clearInterval(timerRef.current);

    return new Promise((resolve) => {
      mediaRef.current.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        mediaRef.current.stream.getTracks().forEach(t => t.stop());
        resolve(blob);
      };
      mediaRef.current.stop();
    });
  };

  const handleStop = async () => {
    const blob = await stopRecording();
    if (!blob || blob.size < 1000) {
      toast.error('Recording too short. Please speak for at least 15 seconds.');
      return;
    }

    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('file', blob, 'recording.webm');
      const token = localStorage.getItem('odapto_session_token') || sessionStorage.getItem('odapto_session_token');
      const res = await fetch(`${API}/company/voice`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        toast.success('Voice recording processed!');
        onComplete(data);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to process recording');
      }
    } catch {
      toast.error('Connection error');
    }
    setProcessing(false);
  };

  useEffect(() => {
    return () => {
      clearInterval(timerRef.current);
      if (mediaRef.current?.state === 'recording') {
        mediaRef.current.stop();
        mediaRef.current.stream?.getTracks().forEach(t => t.stop());
      }
    };
  }, []);

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  if (processing) {
    return (
      <div className="text-center py-8" data-testid="voice-panel">
        <Loader2 className="w-10 h-10 animate-spin mx-auto mb-3 text-odapto-orange" />
        <p className="font-medium mb-1">Processing your recording...</p>
        <p className="text-xs text-muted-foreground">Transcribing and extracting business intelligence</p>
      </div>
    );
  }

  return (
    <div className="text-center py-4" data-testid="voice-panel">
      <p className="text-sm text-muted-foreground mb-4">
        Spend 3 to 5 minutes telling us about your business. Explain what your company does, who your customers are, what makes you different, your biggest challenges and opportunities, and where you want to be in 12 months.
      </p>

      {recording ? (
        <div className="space-y-4">
          <div className="w-20 h-20 rounded-full bg-red-500/10 border-2 border-red-500 flex items-center justify-center mx-auto animate-pulse">
            <Mic className="w-8 h-8 text-red-500" />
          </div>
          <p className="text-2xl font-bold text-red-500">{formatTime(duration)}</p>
          <p className="text-xs text-muted-foreground">Recording... speak naturally</p>
          <Button onClick={handleStop} variant="destructive" className="px-8" data-testid="voice-stop-btn">
            <MicOff className="w-4 h-4 mr-2" /> Stop Recording
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <button
            onClick={startRecording}
            className="w-20 h-20 rounded-full bg-odapto-orange/10 border-2 border-odapto-orange hover:bg-odapto-orange/20 flex items-center justify-center mx-auto transition-colors"
            data-testid="voice-start-btn"
          >
            <Mic className="w-8 h-8 text-odapto-orange" />
          </button>
          <p className="text-sm font-medium">Tap to start recording</p>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════

export function ConfidenceBoost({ kb, onComplete, onSkip }) {
  const [activeOption, setActiveOption] = useState(null); // 'upload' | 'describe' | 'voice'
  const [confidence, setConfidence] = useState(kb?.confidence_score || 0);
  const [prevConfidence] = useState(kb?.confidence_score || 0);

  const handleEnriched = async (data) => {
    if (data?.confidence_score) {
      setConfidence(data.confidence_score);
    }
    // Refresh KB data
    try {
      const res = await apiGet('/company/knowledge-base');
      if (res.ok) {
        const newKb = await res.json();
        setConfidence(newKb.confidence_score || confidence);
        setActiveOption(null);
        if (newKb.confidence_score >= 75) {
          onComplete(newKb);
        }
      }
    } catch { /* ignore */ }
  };

  const handleUploadDone = async () => {
    try {
      const res = await apiGet('/company/knowledge-base');
      if (res.ok) {
        const newKb = await res.json();
        setConfidence(newKb.confidence_score || confidence);
        setActiveOption(null);
        if (newKb.confidence_score >= 75) {
          onComplete(newKb);
        }
      }
    } catch { /* ignore */ }
  };

  const options = [
    {
      key: 'upload',
      icon: Upload,
      title: 'Upload Documents',
      desc: 'Pitch Deck, Business Plan, Revenue Summary, Product Catalogue...',
      color: 'border-blue-500/30 bg-blue-500/5 hover:border-blue-500/60',
    },
    {
      key: 'describe',
      icon: Type,
      title: 'Describe Your Business',
      desc: 'Write a description as if explaining to a new employee',
      color: 'border-emerald-500/30 bg-emerald-500/5 hover:border-emerald-500/60',
    },
    {
      key: 'voice',
      icon: Mic,
      title: 'Speak About Your Business',
      desc: 'Record yourself describing your company for 3-5 minutes',
      color: 'border-violet-500/30 bg-violet-500/5 hover:border-violet-500/60',
    },
  ];

  return (
    <div className="max-w-2xl mx-auto px-4 py-8" data-testid="confidence-boost">
      <div className="text-center mb-8">
        <div className="w-14 h-14 rounded-2xl bg-amber-500/10 flex items-center justify-center mx-auto mb-4">
          <Brain className="w-7 h-7 text-amber-500" />
        </div>
        <h1 className="text-2xl font-bold mb-2">We'd like to understand your business a little better</h1>
        <p className="text-muted-foreground text-sm mb-6">
          Before providing strategic recommendations, Odapto needs sufficient understanding of your company.
        </p>
        <ConfidenceGauge score={confidence} prevScore={prevConfidence} />
        <p className="text-xs text-muted-foreground mt-3">We need at least 75% confidence to provide quality recommendations.</p>
      </div>

      {!activeOption ? (
        <>
          <p className="text-sm font-medium text-center mb-4">Choose how you'd like to help us understand your business:</p>
          <div className="space-y-3 mb-6">
            {options.map(opt => {
              const Icon = opt.icon;
              return (
                <button
                  key={opt.key}
                  onClick={() => setActiveOption(opt.key)}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all ${opt.color}`}
                  data-testid={`boost-option-${opt.key}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-background flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm">{opt.title}</p>
                    <p className="text-xs text-muted-foreground">{opt.desc}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 ml-auto text-muted-foreground shrink-0" />
                </button>
              );
            })}
          </div>
          {confidence >= 50 && (
            <div className="text-center">
              <button onClick={onSkip} className="text-xs text-muted-foreground hover:text-foreground transition-colors underline" data-testid="boost-skip-btn">
                Skip for now and continue with limited intelligence
              </button>
            </div>
          )}
        </>
      ) : (
        <div>
          <button onClick={() => setActiveOption(null)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4">
            <X className="w-4 h-4" /> Back to options
          </button>
          {activeOption === 'upload' && <UploadPanel onComplete={handleUploadDone} />}
          {activeOption === 'describe' && <DescribePanel onComplete={handleEnriched} />}
          {activeOption === 'voice' && <VoicePanel onComplete={handleEnriched} />}
        </div>
      )}
    </div>
  );
}
